// src/components/CircuitEditor.js
import React, { useState, useEffect } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, setDoc, collection, query, where, getDocs, getDoc } from 'firebase/firestore';
import { auth, storage, db } from '../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import ConfirmationDialog from './ConfirmationDialog';
import '../styles.css';
import { updateLastModified } from '../utils/backup';
import { compressImage } from '../utils/imageCompression';

const countries = [
  'Afghanistan', 'Argentina', 'Austria', 'Australia', 'Azerbaijan', 'Bangladesh', 'Belarus', 'Belgium', 'Bolivia', 'Brazil', 'Bulgaria',
  'Cambodia', 'Canada', 'Chile', 'China', 'Colombia', 'Costa Rica', 'Cuba', 'Czech Republic', 'Denmark', 'Dominican Republic',
  'Ecuador', 'El Salvador', 'Finland', 'France', 'Germany', 'Greece', 'Guatemala', 'Haiti', 'Honduras', 'Hungary', 'India',
  'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy', 'Japan', 'Jordan', 'Kazakhstan', 'Kyrgyzstan', 'Laos', 'Lebanon',
  'Malaysia', 'Mexico', 'Myanmar', 'Nepal', 'New Zealand', 'Netherlands', 'Nicaragua', 'North Korea', 'Norway', 'Oman', 'Pakistan',
  'Palestine', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal', 'Romania', 'Russia', 'Saudi Arabia', 'Serbia',
  'Singapore', 'Slovakia', 'South Korea', 'Spain', 'Sri Lanka', 'Sweden', 'Switzerland', 'Syria', 'Taiwan', 'Tajikistan',
  'Thailand', 'Turkey', 'Turkmenistan', 'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States', 'Uzbekistan',
  'Venezuela', 'Vietnam', 'Yemen'
].sort();

export const countryCodes = {
  'Afghanistan': 'AF',
  'Argentina': 'AR',
  'Austria': 'AT',
  'Australia': 'AU',
  'Azerbaijan': 'AZ',
  'Bangladesh': 'BD',
  'Belarus': 'BY',
  'Belgium': 'BE',
  'Bolivia': 'BO',
  'Brazil': 'BR',
  'Bulgaria': 'BG',
  'Cambodia': 'KH',
  'Canada': 'CA',
  'Chile': 'CL',
  'China': 'CN',
  'Colombia': 'CO',
  'Costa Rica': 'CR',
  'Cuba': 'CU',
  'Czech Republic': 'CZ',
  'Denmark': 'DK',
  'Dominican Republic': 'DO',
  'Ecuador': 'EC',
  'El Salvador': 'SV',
  'Finland': 'FI',
  'France': 'FR',
  'Germany': 'DE',
  'Greece': 'GR',
  'Guatemala': 'GT',
  'Haiti': 'HT',
  'Honduras': 'HN',
  'Hungary': 'HU',
  'India': 'IN',
  'Indonesia': 'ID',
  'Iran': 'IR',
  'Iraq': 'IQ',
  'Ireland': 'IE',
  'Israel': 'IL',
  'Italy': 'IT',
  'Japan': 'JP',
  'Jordan': 'JO',
  'Kazakhstan': 'KZ',
  'Kyrgyzstan': 'KG',
  'Laos': 'LA',
  'Lebanon': 'LB',
  'Malaysia': 'MY',
  'Mexico': 'MX',
  'Myanmar': 'MM',
  'Nepal': 'NP',
  'New Zealand': 'NZ',
  'Netherlands': 'NL',
  'Nicaragua': 'NI',
  'North Korea': 'KP',
  'Norway': 'NO',
  'Oman': 'OM',
  'Pakistan': 'PK',
  'Palestine': 'PS',
  'Paraguay': 'PY',
  'Peru': 'PE',
  'Philippines': 'PH',
  'Poland': 'PL',
  'Portugal': 'PT',
  'Romania': 'RO',
  'Russia': 'RU',
  'Saudi Arabia': 'SA',
  'Serbia': 'RS',
  'Singapore': 'SG',
  'Slovakia': 'SK',
  'South Korea': 'KR',
  'Spain': 'ES',
  'Sri Lanka': 'LK',
  'Sweden': 'SE',
  'Switzerland': 'CH',
  'Syria': 'SY',
  'Taiwan': 'TW',
  'Tajikistan': 'TJ',
  'Thailand': 'TH',
  'Turkey': 'TR',
  'Turkmenistan': 'TM',
  'Ukraine': 'UA',
  'United Arab Emirates': 'AE',
  'United Kingdom': 'GB',
  'United States': 'US',
  'Uzbekistan': 'UZ',
  'Venezuela': 'VE',
  'Vietnam': 'VN',
  'Yemen': 'YE',
};

function CircuitEditor({ circuit, onClose, onSave }) {
  const defaultLayout = circuit?.layouts ? circuit.layouts[0] : (circuit?.layout ? { name: circuit.layout, length: circuit.length, image: circuit.image, trackMap: circuit.trackMap } : { name: '', length: '', image: '', trackMap: '' });
  const [name, setName] = useState(circuit?.name || '');
  const [layouts, setLayouts] = useState(circuit?.layouts || [defaultLayout]);
  const [country, setCountry] = useState(circuit?.country || '');
  const [storePublic, setStorePublic] = useState(circuit?.isPublic || false);
  const [notes, setNotes] = useState(circuit?.notes || '');
  const [isAdmin, setIsAdmin] = useState(false);
  const [user] = useAuthState(auth);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [layoutToRemove, setLayoutToRemove] = useState(null);

  // Fetch admin status
  useEffect(() => {
    const fetchAdminStatus = async () => {
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, `users/${user.uid}`));
          if (userDoc.exists()) {
            const adminStatus = userDoc.data().isAdmin === true;
            setIsAdmin(adminStatus);
            // Reset storePublic if user is not admin
            if (!adminStatus) {
              setStorePublic(false);
            }
          }
        } catch (error) {
          console.error('Error fetching admin status:', error);
        }
      }
    };
    fetchAdminStatus();
  }, [user]);

  // Conversion functions
  const kmToMiles = (km) => {
    const miles = parseFloat(km) * 0.621371;
    return isNaN(miles) ? '' : miles.toFixed(3);
  };

  const milesToKm = (miles) => {
    const km = parseFloat(miles) / 0.621371;
    return isNaN(km) ? '' : km.toFixed(3);
  };

  // Handle length input changes
  const handleLengthKmChange = (index, value) => {
    const newLayouts = [...layouts];
    newLayouts[index] = { ...newLayouts[index], length: value };
    setLayouts(newLayouts);
  };

  const handleLengthMiChange = (index, value) => {
    const kmValue = milesToKm(value);
    const newLayouts = [...layouts];
    newLayouts[index] = { ...newLayouts[index], length: kmValue };
    setLayouts(newLayouts);
  };

  const addLayout = () => {
    setLayouts([...layouts, { name: '', length: '', image: '', trackMap: '' }]);
  };

  const removeLayout = (index) => {
    if (layouts.length > 1) {
      const layout = layouts[index];
      const isEmpty = !layout.name && !layout.length && !layout.image && !layout.trackMap;
      if (isEmpty) {
        setLayouts(layouts.filter((_, i) => i !== index));
      } else {
        setLayoutToRemove(index);
        setShowRemoveConfirm(true);
      }
    }
  };

  const confirmRemoveLayout = () => {
    if (layoutToRemove !== null) {
      setLayouts(layouts.filter((_, i) => i !== layoutToRemove));
      setLayoutToRemove(null);
      setShowRemoveConfirm(false);
    }
  };

  const updateLayout = (index, field, value) => {
    const newLayouts = [...layouts];
    newLayouts[index] = { ...newLayouts[index], [field]: value };
    setLayouts(newLayouts);
  };

  // Handle image uploads
  const handleImageUpload = async (e, type, index) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      alert('File size exceeds 10MB limit');
      return;
    }
    const circuitId = circuit?.id || Date.now().toString();
    const path = storePublic
      ? `circuits/${circuitId}/${type}_${index}.png`
      : `circuits/${user.uid}/${circuitId}/${type}_${index}.png`;
    const imageRef = ref(storage, path);
    try {
      const compressionType = type === 'trackmap' ? 'trackmap' : 'event'; // Use 'event' for circuit images
      const compressedFile = await compressImage(file, compressionType);
      await uploadBytes(imageRef, compressedFile);
      const imageUrl = await getDownloadURL(imageRef);
      const newLayouts = [...layouts];
      if (type === 'image') {
        newLayouts[index] = { ...newLayouts[index], image: imageUrl };
      } else {
        newLayouts[index] = { ...newLayouts[index], trackMap: imageUrl };
      }
      setLayouts(newLayouts);
    } catch (err) {
      console.error(`CircuitEditor.js: Error uploading ${type}:`, err);
      alert(`Failed to upload ${type}: ${err.message}`);
    }
  };

  // Check for duplicate circuit names
  const checkDuplicateName = async (circuitName) => {
    if (!user) return false;
    try {
      const circuitsQuery = query(
        collection(db, `users/${user.uid}/circuits`),
        where('name', '==', circuitName)
      );
      const circuitsSnap = await getDocs(circuitsQuery);
      const existingCircuits = circuitsSnap.docs.map(doc => doc.data().name);
      return existingCircuits.includes(circuitName) && circuitName !== circuit?.name;
    } catch (err) {
      console.error('CircuitEditor.js: Error checking duplicate name:', err);
      return false; // Assume no duplicate in case of error to avoid blocking save
    }
  };

  // Save circuit
  const saveCircuit = async () => {
    if (!name || !country) {
      alert('Please fill in circuit name and country');
      return;
    }
    for (let i = 0; i < layouts.length; i++) {
      if (!layouts[i].name || !layouts[i].length) {
        alert(`Please fill in name and length for layout ${i + 1}`);
        return;
      }
    }

    if (await checkDuplicateName(name)) {
      alert('A circuit with this name already exists. Please choose a different name.');
      return;
    }

    const circuitId = circuit?.id || Date.now().toString();
    const filteredLayouts = layouts.filter(layout => layout.name || layout.length || layout.image || layout.trackMap);
    const circuitData = {
      id: circuitId,
      name,
      layouts: filteredLayouts,
      country,
      notes,
      isPublic: storePublic,
      timestamp: Date.now()
    };

    try {
      // Always save to user's private collection
      await setDoc(doc(db, `users/${user.uid}/circuits/${circuitId}`), circuitData);

      // If storePublic is checked and user is admin, also save to global circuits collection
      if (storePublic && isAdmin) {
        await setDoc(doc(db, `circuits/${circuitId}`), circuitData);
      }

      // Update last modified timestamp
      await updateLastModified(user);

      onSave(circuitData);
      onClose();
    } catch (err) {
      console.error('CircuitEditor.js: Error saving circuit:', err);
      alert(`Failed to save circuit: ${err.message}`);
    }
  };

  return (
    <div className="overlay-card">
      <div className="card-container">
        <div className="page-title" style={{ marginBottom: '20px' }}>
          <h1>{circuit ? 'Edit Circuit' : 'Add Custom Circuit'}</h1>
          <button onClick={onClose} className="close-button">✕</button>
        </div>
        <div className="event-editor-details">
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Circuit Name"
            className="event-editor-input"
          />
          <select
            value={country}
            onChange={e => setCountry(e.target.value)}
            className="event-editor-select"
          >
            <option value="">Select Country</option>
            {countries.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          {layouts.map((layout, index) => (
            <div key={index} style={{ border: '1px solid #ccc', padding: '10px', marginBottom: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <input
                  value={layout.name}
                  onChange={e => updateLayout(index, 'name', e.target.value)}
                  placeholder="Layout Name"
                  className="event-editor-input"
                  style={{ flex: 1 }}
                />
                {layouts.length > 1 && (
                  <button
                    onClick={() => removeLayout(index)}
                    className="btn btn-group-action delete"
                    title={`Remove ${layout.name || `Layout ${index + 1}`}`}
                    style={{ marginRight: '15px' }}
                  >
                    −
                  </button>
                )}
              </div>
              <div className="tab-param" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span>Track Length</span>
                <input
                  value={layout.length}
                  onChange={e => handleLengthKmChange(index, e.target.value)}
                  placeholder="km"
                  className="event-editor-input"
                  style={{ width: '100px' }}
                  type="number"
                  step="0.001"
                />
                <span>km</span>
                <input
                  value={layout.length ? (parseFloat(layout.length) * 0.621371).toFixed(3) : ''}
                  onChange={e => handleLengthMiChange(index, e.target.value)}
                  placeholder="mi"
                  className="event-editor-input"
                  style={{ width: '100px' }}
                  type="number"
                  step="0.001"
                />
                <span>mi</span>
              </div>
              <div className="tab-param">
                <span>Image</span>
                <input
                  type="file"
                  onChange={e => handleImageUpload(e, 'image', index)}
                  className="event-editor-input"
                  accept="image/*"
                />
                {layout.image && (
                  <img src={layout.image} alt="Circuit" style={{ maxWidth: '100px', marginTop: '10px' }} />
                )}
              </div>
              <div className="tab-param">
                <span>Track Map</span>
                <input
                  type="file"
                  onChange={e => handleImageUpload(e, 'trackMap', index)}
                  className="event-editor-input"
                  accept="image/*"
                />
                {layout.trackMap && (
                  <img src={layout.trackMap} alt="Track Map" style={{ maxWidth: '100px', marginTop: '10px' }} />
                )}
              </div>
            </div>
          ))}
          <button onClick={addLayout} className="btn btn-secondary">Add Layout</button>
          {isAdmin && (
            <label style={{ marginLeft: '13px' }}>
              Store to Circuit Database
              <input
                type="checkbox"
                checked={storePublic}
                onChange={e => setStorePublic(e.target.checked)}
              />
            </label>
          )}
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Notes"
            className="event-editor-textarea"
          />
          <div className="event-editor-actions">
            <button onClick={saveCircuit} className="btn btn-save">Save</button>
          </div>
        </div>
        {showRemoveConfirm && (
          <ConfirmationDialog
            message={`Are you sure you want to remove the layout "${layouts[layoutToRemove]?.name || `Layout ${layoutToRemove + 1}`}"? This action cannot be undone.`}
            onConfirm={confirmRemoveLayout}
            onCancel={() => setShowRemoveConfirm(false)}
          />
        )}
      </div>
    </div>
  );
}

export default CircuitEditor;